<template>
    <div
        class="field__autocomplete position--relative"
        :class="{ 'field__autocomplete field__autocomplete--active': isOpen }"
        ref="parent"
    >
        <input
            v-bind="$attrs"
            class="field__autocomplete__input position--relative"
            :class="targetClass"
            v-model="model"
            @click="openDrop"
            @keyup.down.prevent="pressArrowDown"
            @keyup.up.prevent="pressArrowUp"
            @keydown.enter.prevent="pressEnter"
        >
        <icon-drop-arrow
            class="field__autocomplete__arrow position--absolute pointer"
            :class="{ 'field__autocomplete__arrow--active': isOpen }"
            @click.native="toggle"
        />
        <div
            class="field__autocomplete__options field__drop position--absolute background--grey--light"
            :class="{ 'field__autocomplete__options--active': isOpen }"
        >
            <ul class="field__autocomplete__options__content field__scroll audio__selector scroll">
                <li
                    v-if="ordering"
                    class="field__autocomplete__option field__drop__option position--relative">
                    {{ $t('global.loading') }}
                    <dot />
                </li>
                <li
                    v-else-if="noResults"
                    class="field__autocomplete__option field__drop__option">{{ $t('global.noResults') }}</li>
                <li
                    v-else
                    v-for="( field, index ) in filterFields"
                    :class="[
                        'field__autocomplete__option field__drop__option audio__selector__option display--grid pointer position--relative',
                        { 'js--active': index ===  selectedOption },
                        { 'display--none': field.hide }
                    ]"
                    :data-id="index"
                    :data-label="field.label"
                    :data-value="field.value"
                    :key="index"
                >
                    <span class="audio__selector__option__action position--absolute"
                        @click="selectOption( $event )"
                        :data-id="index"
                        :data-label="field.label"
                        :data-value="field.value"
                    >
                    </span>
                    <span class="audio__selector__option__label"> {{ field.label }} </span>
                    <base-audio
                        :settings="{
                            id: field.value,
                            requestId: $route.params.id,
                            model: $route.meta.api
                        }"
                    />
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
// package
import {
    reactive,
    toRefs,
    ref
} from '@vue/composition-api';

// hooks
import { baseProps, behavior } from '@/hooks/use-drop';
import { reset } from '@/hooks/use-input-listeners';

// store
import { useActions } from '@/hooks/use-store';

export default {
    inheritAttrs: false,
    components: {
        IconDropArrow: () => import( /* webpackChunkName: 'IconDropArrow' */ '@/components/bundles/layout/images/icons/IconDropArrow' ),
        Dot: () => import( /* webpackChunkName: 'Dot' */ '@/components/bundles/loadings/Dot' ),
        BaseAudio: () => import( /* webpackChunkName: 'BaseAudio' */ '@/components/bundles/players/audio/Audio' )
    },
    props: {
        ...baseProps,
        searchField: Boolean,
        name: String,
        defaultValue: [ String, Number ],
        callId: Number,
        audioUrl: String
    },
    setup ( props, { emit }) {
        const state = reactive({
            parent: null,
            player: null,
            fields: props.data,
            model: props.apiValue?.label,
            value: props.apiValue?.value,
            selectedOption: -1
        });

        // se for um campo usado na pesquisa, vai ter como valor inicial o valor
        // que estiver na query, se existir
        if ( props.searchField )
            state.model = props.defaultValue;

        // listener para quando for necessário
        // proceder a um reset a input.
        reset({ props, object: state, key: 'model' });

        // obtenção do comportamento do autocomplete.
        const { filterFields, noResults, ...navigate } = behavior( props, state );

        // toggle do autocomplete
        const isOpen = ref( false );

        const toggle = () => {
            isOpen.value = !isOpen.value;
            state.model = '';
            state.value = '';
            stopAudio();
        };

        const openDrop = () => {
            isOpen.value = true;
            state.model = '';
            state.value = '';
        };

        const closeDrop = () => {
            isOpen.value = false;
            state.selectedOption = -1;
            stopAudio();
            // validação se existe model ( valor da input )
            // && se não existe valor para ser emitido
            //     caso seja verdade, significa que o utilizador
            //     escreveu o valor sem utilizador a drop, como tal é
            //     necessário chamar a função drop, de forma a obter os valores.
            if ( state.model && !state.value )
                blur();

            // validação se existe valor
            // && se não existe model ( valor da input )
            //     caso seja verdade, é feito o reset do valor.
            if ( state.value && !state.model )
                state.value = '';

            action( state.value );
        };
        // para todos os adios
        const stopAudio = () => {
            const allPlayers = document.querySelectorAll( '.audio__element' );
            allPlayers.forEach( audio => {
                audio.pause();
            });
        };
        // selecção da opção
        const selectOption = $event => {
            state.triggerClick = true;
            defineValues( $event.target.dataset );
            closeDrop();
        };

        // obtenção do valor na escrita
        const blur = () => {
            // caso não exista modelo ou nenhuma opção na
            // filtragem dos campos, então é emitido o campo vazio.
            if ( !state.model || noResults.value ) {
                defineValues({ label: '', value: '' });
                return;
            }

            // caso exista mais que uma opção na filtragem
            // não permite a sua submissão do valor para a store.
            if ( filterFields.value.length > 1 ) {
                defineValues({ label: '', value: '' });
                return;
            }

            defineValues( filterFields.value[0], false );
        };
        // definção do valor e da label.
        const defineValues = ({ label, value }, callCloseDrop = true ) => {
            state.model = label;
            state.value = value;

            if ( callCloseDrop )
                closeDrop();
        };

        // emissão do valor da input para o
        // componente pai.
        const { actions } = useActions();
        const action = value => {
            if ( props.searchField )
                actions( 'saveSearchData', { [props.name]: value });
            else
                emit( 'input', value );
        };

        // seleção da opção através da clea `enter`.
        const pressEnter = () => {
            const selected = state.parent.querySelector( '.js--active' );

            if ( !selected )
                return;

            defineValues( selected.dataset );
        };

        return {
            ...toRefs( state ),
            isOpen,
            toggle,
            openDrop,
            filterFields,
            noResults,
            selectOption,
            pressEnter,
            ...navigate
        };
    }
};
</script>
<style lang="scss">
    .audio{
        &__selector {
            &__option{
                grid-template-columns: repeat(5, 1fr);
                grid-template-rows: 1fr;
                &__label {
                    @include typography-specs( 18px, 24px, 400 );
                    grid-area: 1 / 1 / 2 / 3;
                }
                &__action{
                    @include transform-position( 0, 0, 0, 0 );
                    width: 100%;
                    opacity: 0;
                    z-index: 1;
                }
                .audio{
                    grid-area: 1 / 3 / 2 / 6;
                    width: 230px;
                    z-index: 10;
                    &__player{
                        z-index: 10;
                        &::before{
                            width: 70%;
                        }
                        &--container{
                            z-index: 10;
                        }
                    }
                    &__time{
                        @include typography-specs( 14px, 16px, 400 );
                        width: 83px;
                    }
                }
            }
            .field{
                &__autocomplete{
                    &__option{
                        height: 64px;
                        align-items: center;
                    }
                }
            }
        }
    }

</style>